import { memo } from 'react';
import { 
    Button, 
    Grid, 
    Center, 
    Divider, 
    Flex, 
    Link, 
    Text,
    useColorModeValue,
    useBreakpointValue,
    Box,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

// Utils
import { handleNavigation, handleExternalNavigation } from '../../utils/navigation';

// Options
import { defaultNavigationOptions } from '../navbar/options';

// Components
import Logo from '../ui/Logo';

// Hooks
import { useTranslation } from '../../hooks/useTranslation';

const Footer = memo(() => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Colors
    const bgColor = useColorModeValue('#1f1f1f', '#111111');
    const textColor = useColorModeValue('white', 'white');
    const secondaryTextColor = useColorModeValue('gray.400', 'gray.400');

    const bottomPadding = useBreakpointValue({
        base: '40px',
        md: '60px',
    });

    const handleSocialMedia = (url, event) => {
        handleExternalNavigation(url, event);
    };

    return (
        <Box
            as="footer"
            w="100%"
            minH="400px"
            bg={bgColor}
            color={textColor}
            userSelect="none"
            position="relative"
            overflow="hidden"
            _before={{
                content: '""',
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                height: '2px',
                bg: 'brand.light',
                opacity: 0.6,
            }}
        >
            <Grid
                templateRows="1fr auto auto"
                minH="400px"
                w="100%"
                maxW="100vw"
                px={{ base: 4, md: 8, lg: 12 }}
                py={{ base: 6, md: 8 }}
            >
                {/* Main footer content */}
                <Flex
                    justify="space-evenly"
                    align="center"
                    direction={{ base: 'column', lg: 'row' }}
                    gap={6}
                    flex="1"
                >
                    <Logo 
                        size="default"
                        color={textColor}
                        showHover={true}
                    />
                    
                    <Flex
                        gap={4}
                        textTransform="uppercase"
                        fontSize="14px"
                        display={{ base: 'none', lg: 'flex' }}
                        flexWrap="wrap"
                        justify="center"
                    >
                        {defaultNavigationOptions.map((option, index) => (
                            <Link
                                key={index}
                                mx={2}
                                textDecoration="none"
                                onClick={(e) => handleNavigation(option.link, e, navigate)}
                                _hover={{ color: 'brand.main' }}
                                transition="color 0.2s ease"
                                cursor="pointer"
                            >
                                {t(`navigation.${option.key}`)}
                            </Link>
                        ))}
                    </Flex>
                    
                    <Flex gap={3}>
                        <Button
                            mx={{ base: 1, md: 2 }}
                            bg="transparent"
                            p={0}
                            onClick={(e) => handleSocialMedia('https://twitter.com/SilviaProst', e)}
                            _hover={{ color: 'brand.main', transform: 'scale(1.1)' }}
                            transition="all 0.2s ease"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                            >
                                <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z" />
                            </svg>
                        </Button>
                        <Button
                            mx={{ base: 1, md: 2 }}
                            bg="transparent"
                            p={0}
                            onClick={(e) => handleSocialMedia('https://www.facebook.com/lescuelaqenferma', e)}
                            _hover={{ color: 'brand.main', transform: 'scale(1.1)' }}
                            transition="all 0.2s ease"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                            >
                                <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951" />
                            </svg>
                        </Button>
                        <Button
                            mx={{ base: 1, md: 2 }}
                            bg="transparent"
                            p={0}
                            onClick={(e) => handleSocialMedia('https://www.instagram.com/silviahprost/', e)}
                            _hover={{ color: 'brand.main', transform: 'scale(1.1)' }}
                            transition="all 0.2s ease"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                            >
                                <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.9 3.9 0 0 0-1.417.923A3.9 3.9 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.9 3.9 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.9 3.9 0 0 0-.923-1.417A3.9 3.9 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599s.453.546.598.92c.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.5 2.5 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.5 2.5 0 0 1-.92-.598 2.5 2.5 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233s.008-2.388.046-3.231c.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92s.546-.453.92-.598c.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92m-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217m0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334" />
                            </svg>
                        </Button>
                    </Flex>
                </Flex>
                
                {/* Divider */}
                <Center py={4}>
                    <Divider w="90%" borderColor="gray.600" />
                </Center>
                
                {/* Legal links */}
                <Flex
                    pt={{ base: '20px', md: '10px' }}
                    w="100%"
                    textAlign="center"
                    justify="center"
                    color={secondaryTextColor}
                    flexWrap="wrap"
                    gap={2}
                >
                    <Link
                        mx={2}
                        textDecoration="none"
                        onClick={(e) => handleNavigation('/politica-de-cookies', e, navigate)}
                        _hover={{ color: 'white' }}
                        transition="color 0.2s ease"
                        cursor="pointer"
                    >
                        {t('footer.links.cookies')}
                    </Link>
                    <Text display={{ base: 'none', sm: 'inline' }}>|</Text>
                    <Link
                        mx={2}
                        textDecoration="none"
                        onClick={(e) => handleNavigation('/aviso-legal', e, navigate)}
                        _hover={{ color: 'white' }}
                        transition="color 0.2s ease"
                        cursor="pointer"
                    >
                        {t('footer.links.legal')}
                    </Link>
                    <Text display={{ base: 'none', sm: 'inline' }}>|</Text>
                    <Link
                        mx={2}
                        textDecoration="none"
                        onClick={(e) => handleNavigation('/politica-de-privacidad', e, navigate)}
                        _hover={{ color: 'white' }}
                        transition="color 0.2s ease"
                        cursor="pointer"
                    >
                        {t('footer.links.privacy')}
                    </Link>
                </Flex>
                
                {/* Copyright and developer info */}
                <Flex
                    color={secondaryTextColor}
                    fontSize="14px"
                    justify="space-evenly"
                    direction={{ base: 'column', md: 'row' }}
                    align={{ base: 'center', md: 'stretch' }}
                    py={{ base: 6, md: 4 }}
                    pb={bottomPadding}
                    gap={4}
                >
                    <Box display={{ base: 'none', md: 'block' }} w="33%" />
                    <Text
                        w={{ base: '100%', md: '33%' }}
                        textAlign="center"
                        my={{ base: '20px', md: 0 }}
                    >
                        {t('footer.copyright')}
                    </Text>
                    <Flex
                        w={{ base: '100%', md: '33%' }}
                        justify="center"
                        fontSize={{ base: '12px', md: '14px' }}
                        direction={{ base: 'column', md: 'row' }}
                        align="center"
                        gap={3}
                    >
                        <Text>2024 | Follow me:</Text>
                        <Flex gap={2}>
                            <Box
                                as="a"
                                href="https://www.linkedin.com/in/fabricioalmaraz/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'inherit' }}
                                _hover={{ color: 'white', transform: 'scale(1.1)' }}
                                transition="all 0.2s ease"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    viewBox="0 0 16 16"
                                >
                                    <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854zm4.943 12.248V6.169H2.542v7.225zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248S2.4 3.226 2.4 3.934c0 .694.521 1.248 1.327 1.248zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016l.016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225z" />
                                </svg>
                            </Box>
                            <Box
                                as="a"
                                href="https://github.com/Fabri31295"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'inherit' }}
                                _hover={{ color: 'white', transform: 'scale(1.1)' }}
                                transition="all 0.2s ease"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    viewBox="0 0 16 16"
                                >
                                    <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8" />
                                </svg>
                            </Box>
                        </Flex>
                    </Flex>
                </Flex>
            </Grid>
        </Box>
    );
});

Footer.displayName = 'Footer';

export default Footer;
